import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';

/**
 * Insurensa portal components.
 *
 * The shared building blocks behind both the admin console and the partner
 * portal, styled by `public/theme/insurensa.css`. Both front-ends compose these
 * rather than defining their own layout, so the two surfaces stay consistent and
 * a change to the shell lands in both at once.
 *
 * Page structure:
 *
 *   .app-container.app-theme-white.fixed-header.fixed-sidebar
 *     .app-header.header-shadow
 *     .app-main
 *       .app-sidebar.sidebar-shadow > .app-sidebar__inner > ul.vertical-nav-menu
 *       .app-main__outer > .app-main__inner
 */

// --- Page heading ----------------------------------------------------------

export function PageTitle({
  heading,
  subheading,
  icon = 'pe-7s-note2',
  actions,
}: {
  heading: string;
  subheading?: string;
  /** Pe-icon-7-stroke class, as used throughout the legacy pages. */
  icon?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="app-page-title">
      <div className="page-title-wrapper">
        <div className="page-title-heading">
          <div className="page-title-icon">
            <i className={`${icon} icon-gradient bg-mean-fruit`} />
          </div>
          <div>
            {heading}
            {subheading && <div className="page-title-subheading">{subheading}</div>}
          </div>
        </div>
        {actions && <div className="page-title-actions">{actions}</div>}
      </div>
    </div>
  );
}

// --- Cards -----------------------------------------------------------------

export function Card({
  title,
  actions,
  children,
  className = '',
  noPadding = false,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}) {
  return (
    <div className={`main-card mb-3 card ${className}`}>
      {(title || actions) && (
        <div className="card-header">
          {title}
          {actions && <div className="btn-actions-pane-right">{actions}</div>}
        </div>
      )}
      <div className={noPadding ? '' : 'card-body'}>{children}</div>
    </div>
  );
}

/** The metric tile used across the legacy dashboards. */
export function StatCard({
  label,
  value,
  meta,
  tone = 'primary',
  icon,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  icon?: string;
}) {
  return (
    <div className="card mb-3 widget-content">
      <div className="widget-content-outer">
        <div className="widget-content-wrapper">
          {icon && (
            <div className="widget-content-left mr-3">
              <div className={`icon-wrapper rounded-circle`}>
                <div className={`icon-wrapper-bg bg-${tone}`} />
                <i className={`${icon} text-${tone}`} />
              </div>
            </div>
          )}
          <div className="widget-content-left">
            <div className="widget-heading">{label}</div>
            {meta && <div className="widget-subheading">{meta}</div>}
          </div>
          <div className="widget-content-right">
            <div className={`widget-numbers text-${tone}`}>{value}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Badges ----------------------------------------------------------------

const BADGE_TONES: Record<string, string> = {
  APPROVED: 'success',
  ACCEPTED: 'success',
  PENDING: 'warning',
  DECLINED: 'danger',
  REJECTED: 'danger',
  WITHDRAWN: 'secondary',
};

export function Badge({
  children,
  tone,
  value,
  pill = true,
}: {
  children: ReactNode;
  tone?: string;
  value?: string;
  pill?: boolean;
}) {
  const resolved = tone ?? (value ? (BADGE_TONES[value] ?? 'secondary') : 'secondary');
  return (
    <div className={`badge badge-${resolved} ${pill ? 'badge-pill' : ''}`}>{children}</div>
  );
}

// --- Feedback --------------------------------------------------------------

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}) {
  return (
    <div className={`alert alert-${tone} fade show`} role="alert">
      {children}
    </div>
  );
}

/** Star rating, matching the header widget in `partner/header.php`. */
export function Stars({ value, showValue = false }: { value: number; showValue?: boolean }) {
  const stars = [];
  for (let index = 1; index <= 5; index += 1) {
    if (value >= index) stars.push(<i key={index} className="fa fa-star yellow-color" />);
    else if (value >= index - 0.5) stars.push(<i key={index} className="fa fa-star-half-alt yellow-color" />);
    else stars.push(<i key={index} className="fa fa-star-o text-muted" />);
  }

  return (
    <span aria-label={`${value.toFixed(1)} von 5 Sternen`}>
      {showValue && <b className="mr-1">{value.toFixed(1)}</b>}
      {stars}
    </span>
  );
}

// --- Definition list used on every detail page -----------------------------

export function PropertyList({ children }: { children: ReactNode }) {
  return <table className="table table-sm table-borderless mb-0 property-list">{children}</table>;
}

export function Property({ label, children }: { label: string; children: ReactNode }) {
  return (
    <tr>
      <th style={{ width: '40%', fontWeight: 500, color: '#6c757d' }}>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

// --- Shell -----------------------------------------------------------------

export interface NavEntry {
  href: string;
  label: string;
  icon?: string;
  badge?: number;
  /** Renders as a collapsible group, as the legacy "Übersicht Leads" menu did. */
  children?: NavEntry[];
}

export function AppShell({
  brandLogo = '/theme/assets/images/logo.png',
  headerRight,
  sidebar,
  children,
  sidebarClosed = false,
  onToggleSidebar,
}: {
  brandLogo?: string;
  headerRight?: ReactNode;
  sidebar: ReactNode;
  children: ReactNode;
  /** Desktop only: collapse the sidebar to its icon rail. */
  sidebarClosed?: boolean;
  onToggleSidebar?: () => void;
}) {
  /**
   * The mobile drawer is separate state from the desktop collapse.
   *
   * They were previously the same flag, which meant the desktop "collapse"
   * class was applied on phones too — the theme then swapped in its narrow
   * logo variant and the wordmark rendered at its natural size over the
   * header. It also left the drawer permanently off-canvas at -280px with no
   * way to open it, so phones had no navigation at all.
   */
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the drawer once the viewport is wide enough to show the sidebar
  // permanently, so it cannot be left in a stuck state after a rotation.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const sync = (): void => { if (mq.matches) setMobileOpen(false); };
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Any navigation inside the drawer should dismiss it.
  const closeOnNavigate = (event: MouseEvent<HTMLDivElement>): void => {
    if ((event.target as HTMLElement).closest('a')) setMobileOpen(false);
  };

  return (
    <div
      className={[
        'app-container app-theme-white body-tabs-shadow fixed-header fixed-sidebar',
        sidebarClosed ? 'closed-sidebar' : '',
        mobileOpen ? 'sidebar-mobile-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="app-header header-shadow d-print-none">
        <div className="app-header__logo">
          <div className="logo-src">
            <img src={brandLogo} className="logo_img" alt="Insurensa" />
          </div>
          <div className="header__pane ml-auto">
            <button
              type="button"
              className="hamburger close-sidebar-btn hamburger--elastic"
              onClick={onToggleSidebar}
              aria-label="Seitenleiste umschalten"
              aria-expanded={!sidebarClosed}
            >
              <span className="hamburger-box">
                <span className="hamburger-inner" />
              </span>
            </button>
          </div>
        </div>

        <div className="app-header__mobile-menu">
          <button
            type="button"
            className={`hamburger hamburger--elastic mobile-toggle-nav${mobileOpen ? ' is-active' : ''}`}
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Menü öffnen"
            aria-expanded={mobileOpen}
          >
            <span className="hamburger-box">
              <span className="hamburger-inner" />
            </span>
          </button>
        </div>

        <div className="app-header__content">
          <div className="app-header-left" />
          <div className="app-header-right">{headerRight}</div>
        </div>
      </div>

      <div className="app-main">
        {/* Tapping outside the open drawer dismisses it, as a drawer should. */}
        <div
          className="sidebar-backdrop"
          hidden={!mobileOpen}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        <div className="app-sidebar sidebar-shadow d-print-none" onClick={closeOnNavigate}>
          <div className="scrollbar-sidebar">
            <div className="app-sidebar__inner">{sidebar}</div>
          </div>
        </div>

        <div className="app-main__outer">
          <div className="app-main__inner">{children}</div>
          <div className="app-wrapper-footer">
            <div className="app-footer">
              <div className="app-footer__inner">
                <div className="app-footer-left">
                  <span className="text-muted">
                    Copyright © Insurensa {new Date().getFullYear()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SidebarNav({
  heading = 'Menu',
  entries,
  isActive,
  renderLink,
}: {
  heading?: string;
  entries: NavEntry[];
  isActive: (href: string) => boolean;
  renderLink: (entry: NavEntry, className: string, content: ReactNode) => ReactNode;
}) {
  const content = (entry: NavEntry): ReactNode => (
    <>
      <i className={`metismenu-icon ${entry.icon ?? ''}`} />
      {entry.label}
      {entry.badge !== undefined && entry.badge > 0 && (
        <div className="ml-auto badge badge-pill badge-warning">{entry.badge}</div>
      )}
    </>
  );

  return (
    <ul className="vertical-nav-menu">
      <li className="app-sidebar__heading">{heading}</li>
      {entries.map((entry) => {
        if (entry.children && entry.children.length > 0) {
          const groupActive = entry.children.some((child) => isActive(child.href));
          return (
            <li key={entry.label} className={groupActive ? 'mm-active open' : 'open'}>
              <a href="#" onClick={(event) => event.preventDefault()}>
                <i className={`metismenu-icon ${entry.icon ?? ''}`} />
                {entry.label}
                <i className="metismenu-state-icon pe-7s-angle-down caret-left" />
              </a>
              <ul className="mm-show">
                {entry.children.map((child) => (
                  <li key={child.href} className={isActive(child.href) ? 'mm-active' : ''}>
                    {renderLink(child, isActive(child.href) ? 'mm-active' : '', content(child))}
                  </li>
                ))}
              </ul>
            </li>
          );
        }

        return (
          <li key={entry.href} className={isActive(entry.href) ? 'mm-active' : ''}>
            {renderLink(entry, isActive(entry.href) ? 'mm-active' : '', content(entry))}
          </li>
        );
      })}
    </ul>
  );
}
